import { z } from "zod";

const RequestIdSchema = z.string().regex(/^req_[0-9a-f-]{36}$/u);
const UuidSchema = z.uuid();

export const McpScopeSchema = z.enum([
  "workspace:read",
  "organization:write",
  "tasks:read",
  "tasks:write",
  "routines:read",
  "routines:write",
  "reviews:read",
  "reviews:write",
  "notifications:read",
  "notifications:write",
  "evidence:metadata:read",
  "insights:read",
]);

export const OAuthClientRegistrationRequestSchema = z
  .object({
    client_name: z.string().trim().min(1).max(120),
    redirect_uris: z.array(z.url().max(2048)).min(1).max(10),
    token_endpoint_auth_method: z.literal("none").default("none"),
    grant_types: z
      .array(z.enum(["authorization_code", "refresh_token"]))
      .default(["authorization_code", "refresh_token"]),
    response_types: z.array(z.literal("code")).default(["code"]),
  })
  .strict();

export const OAuthClientRegistrationResponseSchema = z
  .object({
    client_id: UuidSchema,
    client_name: z.string(),
    redirect_uris: z.array(z.url()),
    token_endpoint_auth_method: z.literal("none"),
    grant_types: z.array(z.enum(["authorization_code", "refresh_token"])),
    response_types: z.array(z.literal("code")),
    client_id_issued_at: z.number().int().nonnegative(),
  })
  .strict();

export const OAuthAuthorizationRequestCreateSchema = z
  .object({
    client_id: UuidSchema,
    redirect_uri: z.url().max(2048),
    response_type: z.literal("code"),
    scope: z.string().trim().min(1).max(1000),
    state: z.string().min(1).max(1000).nullable(),
    code_challenge: z.string().regex(/^[A-Za-z0-9_-]{43,128}$/u),
    code_challenge_method: z.literal("S256"),
  })
  .strict();

export const OAuthAuthorizationRequestCreateResponseSchema = z
  .object({
    authorization_request_id: UuidSchema,
    consent_url: z.url(),
    expires_at: z.iso.datetime(),
    request_id: RequestIdSchema,
  })
  .strict();

export const OAuthConsentRequestSchema = z
  .object({
    id: UuidSchema,
    client_id: UuidSchema,
    client_name: z.string(),
    requested_scopes: z.array(McpScopeSchema),
    expires_at: z.iso.datetime(),
  })
  .strict();

export const OAuthConsentResponseSchema = z
  .object({
    authorization_request: OAuthConsentRequestSchema,
    request_id: RequestIdSchema,
  })
  .strict();

export const OAuthConsentApproveRequestSchema = z
  .object({
    approved_scopes: z.array(McpScopeSchema).min(1).max(20),
    child_scope: z.enum(["selected", "all"]),
    child_ids: z.array(UuidSchema).max(20),
    expires_in_days: z.number().int().min(1).max(90),
  })
  .strict()
  .refine((value) => value.child_scope === "selected" || value.child_ids.length === 0, {
    message: "family-wide grants do not accept child_ids",
    path: ["child_ids"],
  });

export const OAuthConsentApproveResponseSchema = z
  .object({
    redirect_uri: z.url(),
    request_id: RequestIdSchema,
  })
  .strict();

export const OAuthTokenResponseSchema = z
  .object({
    access_token: z.string().min(32),
    token_type: z.literal("Bearer"),
    expires_in: z.number().int().positive(),
    refresh_token: z.string().min(32),
    scope: z.string(),
  })
  .strict();

export const McpActorResponseSchema = z
  .object({
    actor: z.object({
      account_id: UuidSchema,
      workspace_id: UuidSchema,
      workspace_name: z.string(),
      workspace_timezone: z.string(),
      membership_id: UuidSchema,
      membership_role: z.enum(["owner", "guardian"]),
      grant_id: UuidSchema,
      client_id: UuidSchema,
      scopes: z.array(McpScopeSchema),
      child_scope: z.enum(["selected", "all"]),
      child_ids: z.array(UuidSchema),
      expires_at: z.iso.datetime(),
    }),
    request_id: RequestIdSchema,
  })
  .strict();

export const McpToolAuditRequestSchema = z
  .object({
    tool_name: z.string().regex(/^oyako_[a-z0-9_]+_v[1-9][0-9]*$/u),
    scope: McpScopeSchema,
    target_ids: z.array(UuidSchema).max(50),
    outcome: z.enum(["success", "failure"]),
    command_id: z.string().min(1).max(200).nullable(),
  })
  .strict();

export const McpResourceAuditRequestSchema = z
  .object({
    resource_uri: z
      .string()
      .regex(/^oyako:\/\/[A-Za-z0-9/_-]+$/u)
      .max(2048),
    outcome: z.enum(["success", "failure"]),
  })
  .strict();

export const McpBulkRescheduleChangeRequestSchema = z
  .object({
    child_id: UuidSchema,
    task_id: UuidSchema,
    assignment_id: UuidSchema,
    expected_version: z.number().int().positive(),
    planned_local_date: z.iso.date(),
    due_local_date: z.iso.date().nullable(),
  })
  .strict()
  .refine(
    (value) => value.due_local_date === null || value.due_local_date >= value.planned_local_date,
    { message: "due date must not be earlier than planned date", path: ["due_local_date"] },
  );

export const McpBulkReschedulePreviewRequestSchema = z
  .object({ changes: z.array(McpBulkRescheduleChangeRequestSchema).min(2).max(20) })
  .strict()
  .refine(
    (value) => new Set(value.changes.map((change) => change.task_id)).size === value.changes.length,
    { message: "task_id must be unique", path: ["changes"] },
  );

export const McpBulkReschedulePreviewChangeSchema = McpBulkRescheduleChangeRequestSchema.extend({
  task_title: z.string().min(1).max(240),
  previous_planned_local_date: z.iso.date().nullable(),
  previous_due_local_date: z.iso.date().nullable(),
}).strict();

export const McpBulkReschedulePreviewSchema = z
  .object({
    id: UuidSchema,
    affected_count: z.number().int().min(2).max(20),
    expires_at: z.iso.datetime(),
    changes: z.array(McpBulkReschedulePreviewChangeSchema).min(2).max(20),
  })
  .strict();

export const McpBulkReschedulePreviewResponseSchema = z
  .object({
    preview: McpBulkReschedulePreviewSchema,
    idempotency_replayed: z.boolean(),
    request_id: RequestIdSchema,
  })
  .strict();

export const McpBulkRescheduleApplyRequestSchema = z
  .object({
    expected_affected_count: z.number().int().min(2).max(20),
    confirmation: z.literal("apply_preview"),
  })
  .strict();

export const McpBulkRescheduleAppliedChangeSchema = z
  .object({
    child_id: UuidSchema,
    task_id: UuidSchema,
    assignment_id: UuidSchema,
    version: z.number().int().positive(),
    planned_local_date: z.iso.date(),
    due_local_date: z.iso.date().nullable(),
  })
  .strict();

export const McpBulkRescheduleApplyResponseSchema = z
  .object({
    preview_id: UuidSchema,
    affected_count: z.number().int().min(2).max(20),
    changes: z.array(McpBulkRescheduleAppliedChangeSchema).min(2).max(20),
    sync_cursor: z.string().min(1),
    idempotency_replayed: z.boolean(),
    request_id: RequestIdSchema,
  })
  .strict();

export const AgentGrantSchema = z
  .object({
    id: UuidSchema,
    client_id: UuidSchema,
    client_name: z.string(),
    scopes: z.array(McpScopeSchema),
    child_scope: z.enum(["selected", "all"]),
    child_ids: z.array(UuidSchema),
    expires_at: z.iso.datetime(),
    revoked_at: z.iso.datetime().nullable(),
    created_at: z.iso.datetime(),
    version: z.number().int().positive(),
  })
  .strict();

export const AgentGrantListResponseSchema = z
  .object({ items: z.array(AgentGrantSchema), request_id: RequestIdSchema })
  .strict();

export const AgentGrantRevokeRequestSchema = z
  .object({ version: z.number().int().positive() })
  .strict();
